import { type CSSProperties, useMemo, useState } from 'react';
import classNames from 'clsx';
import { diffJson } from 'diff';

import { groupChangesForCollapse } from './diffCollapse';
import { diffJsonStructurally, parseJsonPair } from './jsonStructuralDiff';
import { renderCollapsibleSplitDiff, renderSplitDiff } from './splitDiffRenderer';
import { diffByLine, diffJSONByLine, lineNumberGutterExtraWidth } from './utils';

import styles from './Diff.module.css';

type SplitDiffViewProps = {
  original: string;
  compare: string;
  showLineNumbers?: boolean;
  includeSubDiffs?: boolean;
  title?: string;
  isJSON?: boolean;
  alignJsonByKeyPath?: boolean;
  className?: string;
  contextLines?: number;
};

export function SplitDiff({
  original,
  compare,
  showLineNumbers = true,
  title,
  includeSubDiffs = false,
  isJSON = false,
  alignJsonByKeyPath = false,
  className,
  contextLines = 0,
}: SplitDiffViewProps) {
  const isSameDocument = useMemo(() => original === compare, [original, compare]);

  const diff = useMemo(() => {
    if (isJSON) {
      if (alignJsonByKeyPath) {
        const parsed = parseJsonPair(original, compare);
        if (parsed) {
          return diffJsonStructurally(parsed[0], parsed[1]);
        }
      }

      if (showLineNumbers) {
        return diffJSONByLine(original, compare);
      }
      return diffJson(original, compare);
    }
    return diffByLine(original, compare);
  }, [original, compare, isJSON, showLineNumbers, alignJsonByKeyPath]);

  const groups = useMemo(() => (contextLines ? groupChangesForCollapse(diff, contextLines) : []), [diff, contextLines]);

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const handleToggleExpand = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIndex)) {
        next.delete(groupIndex);
      } else {
        next.add(groupIndex);
      }
      return next;
    });
  };

  const gutterStyle = useMemo(
    () => ({ '--diff-gutter-extra': lineNumberGutterExtraWidth(diff, contextLines > 0) }) as CSSProperties,
    [diff, contextLines],
  );

  const { leftElements, rightElements } =
    contextLines > 0
      ? renderCollapsibleSplitDiff(
          groups,
          expandedGroups,
          showLineNumbers,
          includeSubDiffs,
          contextLines,
          handleToggleExpand,
        )
      : renderSplitDiff(diff, showLineNumbers, includeSubDiffs);

  return (
    <div
      className={classNames(styles.diffView, { [styles.diffViewSameDoc]: isSameDocument })}
      style={showLineNumbers ? gutterStyle : undefined}
    >
      <div className={styles.controls}>{title && <div className={styles.title}>{title}</div>}</div>

      <div className={classNames(styles.splitDiff, className)} data-test-id="split-diff-container">
        <div className={styles.splitDiffLeft} data-test-id="split-diff-left">
          {leftElements}
        </div>
        <div className={styles.splitDiffRight} data-test-id="split-diff-right">
          {rightElements}
        </div>
      </div>
    </div>
  );
}
